import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Trophy,
  DollarSign,
  Instagram,
  Calendar,
  CheckCircle,
  Sparkles,
  Users,
  Crown,
  ArrowRight,
  Star,
  Gift,
  Scissors,
  Building2,
  Camera,
  Hash,
  MapPin,
  Mail,
  Award,
  Zap,
  ChevronRight,
  Percent,
  Banknote,
  Repeat,
  Heart,
  Share2,
  MessageSquare,
  Facebook,
  Twitter,
  Link as LinkIcon,
  Copy,
  Check,
  Shield,
  Loader2,
  X
} from "lucide-react";
import AppFooter from "@/components/AppFooter";
import SEOMeta from "@/components/SEOMeta";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const LookFestPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [copied, setCopied] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const shareMenuRef = useRef(null);
  
  // Soldier code state
  const [soldierCode, setSoldierCode] = useState("");
  const [soldierValidation, setSoldierValidation] = useState(null);
  const [validatingSoldier, setValidatingSoldier] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  
  // Check for soldier code in URL on mount
  useEffect(() => {
    const codeFromUrl = searchParams.get("soldier") || searchParams.get("code") || "";
    if (codeFromUrl) {
      setSoldierCode(codeFromUrl.toUpperCase());
      validateSoldierCode(codeFromUrl);
    }
  }, [searchParams]);
  
  // Validate soldier code
  const validateSoldierCode = async (code) => {
    if (!code.trim()) return;
    
    setValidatingSoldier(true);
    try {
      const res = await axios.get(`${API}/api/membership/promo/validate/${code.trim()}`);
      if (res.data.valid) {
        setSoldierValidation(res.data);
        toast.success(`🎉 ${res.data.trial_days}-day FREE trial unlocked!`);
      } else {
        setSoldierValidation({ valid: false });
      }
    } catch (error) {
      setSoldierValidation({ valid: false });
    } finally {
      setValidatingSoldier(false);
    }
  };
  
  // Handle manual code entry
  const handleApplyCode = () => {
    if (soldierCode.trim()) {
      validateSoldierCode(soldierCode);
    }
  };
  
  // Navigate to register with soldier code
  const handleEnterContest = (role = 'stylist') => {
    let url = `/register?contest=lookfest&role=${role}`;
    if (soldierCode && soldierValidation?.valid) {
      url += `&soldier=${soldierCode}`;
    }
    navigate(url);
  };
  
  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target)) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Share URL and message
  const shareUrl = "https://myhairchair.com/lookfest";
  const shareTitle = "Keep the 100 Look Fest - Win Cash Prizes!";
  const shareMessage = "🔥 Check out the Keep the 100 Look Fest! Win $100 monthly + compete for $1,000 grand prize. All stylists & barbers welcome! Join at:";

  // Campaign images - Diverse stylists with Keep the 100 banners
  const campaignImages = {
    hero: "https://static.prod-images.emergentagent.com/jobs/675f906d-89d5-4d72-be4d-b52475a90edd/images/33180f0f302fe8b5af513d78e245ca72dedd527a658a30b4cc710eb985dee235.png",
    ogShareImage: "https://static.prod-images.emergentagent.com/jobs/38ebe32d-8430-4fa2-9b49-c7f0abf8baab/images/5935932715bd9ab6cbc6057cda3fafc0b766dae53b1f81b71fd6a14283584fa2.png",
    blackMaleBarber: "https://static.prod-images.emergentagent.com/jobs/675f906d-89d5-4d72-be4d-b52475a90edd/images/76dd764acda10b521fee7ccb7dc4252eda1b4868c519c799bf5d140e4835ba42.png",
    whiteFemaleStyleist: "https://static.prod-images.emergentagent.com/jobs/675f906d-89d5-4d72-be4d-b52475a90edd/images/1ec7db32ec1635024f2b13937e297b59f3f1aab33d961b8b8c1f03a12807cae4.png",
    blackFemaleBraider: "https://static.prod-images.emergentagent.com/jobs/675f906d-89d5-4d72-be4d-b52475a90edd/images/5d93856cca37067731995ac3a03f8696bc988ad551b6d60308d79564d832270f.png",
    latinoBarber: "https://static.prod-images.emergentagent.com/jobs/675f906d-89d5-4d72-be4d-b52475a90edd/images/066e31b10b34c06aeff1508f381bf53e4f82a0b8481a4408e6271d8e5d153f8c.png"
  };

  // Share functions
  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Link copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareSMS = () => {
    const smsBody = encodeURIComponent(`${shareMessage} ${shareUrl}`);
    window.open(`sms:?body=${smsBody}`, '_blank');
  };

  const handleShareEmail = () => {
    const subject = encodeURIComponent(shareTitle);
    const body = encodeURIComponent(`${shareMessage}\n\n${shareUrl}\n\n#KeepThe100 #LookFest #MyHairChair`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const handleShareTwitter = () => {
    const text = encodeURIComponent(`${shareMessage} ${shareUrl} #KeepThe100 #LookFest`);
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
  };

  const handleShareFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  const handleShareWhatsApp = () => {
    const text = encodeURIComponent(`${shareMessage} ${shareUrl}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const handleShareInstagram = () => {
    // Instagram doesn't have a direct share URL, so copy link and prompt user
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied! Open Instagram and paste in your story or bio.");
  };

  const monthlyPrizes = [
    { icon: DollarSign, text: "$100 Cash - Instant Payout", highlight: true },
    { icon: Crown, text: "1 Month Premium Credit" },
    { icon: Star, text: "National Feature on MHC Socials" }
  ];

  const grandPrizes = [
    { place: "1st", cash: "$1,000", access: "1 Year Free", color: "from-yellow-400 to-amber-500", textColor: "text-black" },
    { place: "2nd", cash: "$500", access: "6 Months Free", color: "from-gray-300 to-gray-400", textColor: "text-black" },
    { place: "3rd", cash: "$250", access: "3 Months Free", color: "from-amber-600 to-orange-700", textColor: "text-white" }
  ];

  const whyKeepThe100 = [
    {
      icon: Banknote,
      title: "CashApp Payments",
      description: "Clients pay you directly. You keep every dollar.",
      highlight: "0% fees"
    },
    {
      icon: DollarSign,
      title: "Zelle Transfers",
      description: "Instant bank transfers from clients. No middleman.",
      highlight: "0% fees"
    },
    {
      icon: Percent,
      title: "Cash Payments",
      description: "The original 0% fee method. Your money, your pocket.",
      highlight: "0% fees"
    }
  ];

  const howItWorks = [
    {
      step: 1,
      title: "Register on MyHairChair",
      description: soldierValidation?.valid 
        ? `Create your FREE account. You get a ${soldierValidation.trial_days}-day Premium trial with your referral code!`
        : "Create your FREE account. All contest entries get a 30-day Premium trial!",
      icon: Users
    },
    {
      step: 2,
      title: "Post Your Best Looks",
      description: "Share your client transformations to OUR Instagram @myhairchairhq. Tag us and use #KeepThe100LookFest. Post as many looks as you want!",
      icon: Instagram
    },
    {
      step: 3,
      title: "Get Verified & Win",
      description: "Our team verifies your entries. More posts = more chances! Winners announced on the 1st of each month.",
      icon: Trophy
    }
  ];

  const timeline = [
    { month: "FEB", status: "Round 1" },
    { month: "MAR", status: "Round 2" },
    { month: "APR", status: "Round 3" },
    { month: "MAY", status: "Round 4" },
    { month: "JUN", status: "Round 5" },
    { month: "JUL", status: "Round 6" },
    { month: "AUG", status: "Round 7" },
    { month: "SEP", status: "Round 8" },
    { month: "OCT", status: "FINALE", highlight: true }
  ];

  const winners = [
    { image: campaignImages.blackMaleBarber, name: "Your Name Here", location: "Your City" },
    { image: campaignImages.whiteFemaleStyleist, name: "Could Be You", location: "Your State" },
    { image: campaignImages.latinoBarber, name: "Win Big", location: "Anywhere USA" },
    { image: campaignImages.blackFemaleBraider, name: "Next Winner", location: "Your Town" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags for Social Sharing */}
      <SEOMeta 
        title="Keep the 100 Look Fest | Win Cash Prizes | MyHairChair"
        description="Join the biggest stylist competition! Post your best looks, win $100 monthly + compete for $1,000 grand prize. All stylists, barbers & salon owners welcome. Keep 100% of your fees!"
        image={campaignImages.ogShareImage}
        url="https://myhairchair.com/lookfest"
        type="website"
      />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-b from-amber-50 to-white">
        {/* Header */}
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div 
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => navigate('/')}
            >
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
                <Scissors className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">MyHairChair</span>
            </div>
            <Button
              variant="outline"
              className="border-amber-500 text-amber-600 hover:bg-amber-50"
              onClick={() => navigate('/login')}
            >
              Sign In
            </Button>
          </div>
        </div>

        {/* Hero Content */}
        <div className="max-w-7xl mx-auto px-4 py-8 sm:py-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text */}
            <div className="text-center lg:text-left">
              <Badge className="bg-amber-100 text-amber-700 border-amber-300 mb-6 px-4 py-2 text-sm">
                <Calendar className="w-4 h-4 mr-2" />
                February 2026 - October 2026
              </Badge>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 mb-4">
                Keep the <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-orange-600">100</span>
              </h1>
              <h2 className="text-3xl sm:text-4xl font-bold text-amber-500 mb-6">
                Look Fest
              </h2>
              
              <p className="text-xl sm:text-2xl text-gray-700 mb-4 font-medium">
                "You keep 100% of the fees; we give you 100% of the spotlight."
              </p>
              <p className="text-lg text-gray-500 mb-6 max-w-xl">
                Show off your best client looks on our Instagram. Win cash prizes every month. 
                Join the movement of stylists who keep ALL their money.
              </p>

              {/* Soldier Code Link - Top of Hero */}
              {soldierValidation?.valid ? (
                <div className="bg-gradient-to-r from-purple-100 to-pink-100 border border-purple-300 rounded-xl p-4 mb-6 max-w-md mx-auto lg:mx-0">
                  <div className="flex items-center gap-3">
                    <Shield className="w-6 h-6 text-purple-600" />
                    <div className="flex-1">
                      <p className="font-bold text-purple-800">Referred by {soldierValidation.salon_name}</p>
                      <p className="text-sm text-purple-600">
                        <span className="font-mono font-bold">{soldierCode}</span> • {soldierValidation.trial_days}-Day FREE Trial
                      </p>
                    </div>
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  </div>
                </div>
              ) : (
                <div className="mb-6">
                  {!showCodeInput ? (
                    <button
                      onClick={() => setShowCodeInput(true)}
                      className="text-purple-600 hover:text-purple-700 font-medium flex items-center gap-2 mx-auto lg:mx-0"
                    >
                      <Gift className="w-4 h-4" />
                      Have a Soldier referral code? Enter it here
                    </button>
                  ) : (
                    <div className="bg-white border-2 border-purple-200 rounded-xl p-4 max-w-sm mx-auto lg:mx-0">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
                          <Shield className="w-4 h-4 text-purple-500" />
                          Enter Soldier Code
                        </span>
                        <button onClick={() => setShowCodeInput(false)} className="text-gray-400 hover:text-gray-600">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <Input
                          placeholder="e.g. GEORGE-HQ"
                          value={soldierCode}
                          onChange={(e) => setSoldierCode(e.target.value.toUpperCase())}
                          className="flex-1 text-center font-mono uppercase border-purple-200"
                        />
                        <Button
                          onClick={handleApplyCode}
                          disabled={validatingSoldier || !soldierCode.trim()}
                          size="sm"
                          className="bg-purple-500 hover:bg-purple-600 text-white"
                        >
                          {validatingSoldier ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-bold text-lg px-8 py-6 shadow-lg shadow-amber-500/30"
                  onClick={() => handleEnterContest('stylist')}
                  data-testid="lookfest-enter-btn"
                >
                  <Trophy className="w-5 h-5 mr-2" />
                  Enter the Contest
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                
                {/* Share Button with Dropdown */}
                <div className="relative" ref={shareMenuRef}>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-amber-500 text-amber-600 hover:bg-amber-50 font-bold text-lg px-6 py-6"
                    onClick={() => setShowShareMenu(!showShareMenu)}
                    data-testid="lookfest-share-btn"
                  >
                    <Share2 className="w-5 h-5 mr-2" />
                    Share Contest
                  </Button>
                  
                  {/* Share Dropdown Menu */}
                  {showShareMenu && (
                    <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2">
                      <div className="p-2">
                        <p className="text-xs text-gray-500 px-3 py-2 font-medium">Share via</p>
                        
                        <button
                          onClick={handleShareSMS}
                          className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-amber-50 rounded-lg transition-colors"
                          data-testid="share-sms-btn"
                        >
                          <MessageSquare className="w-5 h-5 text-green-600" />
                          <span className="font-medium text-gray-700">Text Message (SMS)</span>
                        </button>
                        
                        <button
                          onClick={handleShareEmail}
                          className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-amber-50 rounded-lg transition-colors"
                          data-testid="share-email-btn"
                        >
                          <Mail className="w-5 h-5 text-blue-600" />
                          <span className="font-medium text-gray-700">Email</span>
                        </button>
                        
                        <button
                          onClick={handleShareWhatsApp}
                          className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-amber-50 rounded-lg transition-colors"
                          data-testid="share-whatsapp-btn"
                        >
                          <MessageSquare className="w-5 h-5 text-green-500" />
                          <span className="font-medium text-gray-700">WhatsApp</span>
                        </button>
                        
                        <div className="border-t border-gray-100 my-1"></div>
                        
                        <button
                          onClick={handleShareTwitter}
                          className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-amber-50 rounded-lg transition-colors"
                          data-testid="share-twitter-btn"
                        >
                          <Twitter className="w-5 h-5 text-sky-500" />
                          <span className="font-medium text-gray-700">Twitter / X</span>
                        </button>
                        
                        <button
                          onClick={handleShareFacebook}
                          className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-amber-50 rounded-lg transition-colors"
                          data-testid="share-facebook-btn"
                        >
                          <Facebook className="w-5 h-5 text-blue-600" />
                          <span className="font-medium text-gray-700">Facebook</span>
                        </button>
                        
                        <button
                          onClick={handleShareInstagram}
                          className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-amber-50 rounded-lg transition-colors"
                          data-testid="share-instagram-btn"
                        >
                          <Instagram className="w-5 h-5 text-pink-600" />
                          <span className="font-medium text-gray-700">Instagram (Copy Link)</span>
                        </button>
                        
                        <div className="border-t border-gray-100 my-1"></div>
                        
                        <button
                          onClick={handleCopyLink}
                          className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-amber-50 rounded-lg transition-colors"
                          data-testid="share-copy-link-btn"
                        >
                          {copied ? (
                            <>
                              <Check className="w-5 h-5 text-green-600" />
                              <span className="font-medium text-green-600">Copied!</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-5 h-5 text-gray-600" />
                              <span className="font-medium text-gray-700">Copy Link</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Trial Badge */}
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium">
                <Gift className="w-4 h-4" />
                All contest entries include a FREE 30-day Premium trial!
              </div>
            </div>

            {/* Right - Hero Image */}
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl shadow-amber-500/20 border-4 border-amber-100">
                <img 
                  src={campaignImages.hero} 
                  alt="Diverse group of stylists and barbers celebrating with Keep the 100 banner"
                  className="w-full h-auto object-cover"
                  style={{ minHeight: '400px' }}
                />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-xl p-4 border border-amber-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Instant Payouts</p>
                    <p className="text-sm text-gray-500">Via Zelle or CashApp</p>
                  </div>
                </div>
              </div>
              {/* Multiple entries badge */}
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl p-4 border border-purple-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center">
                    <Repeat className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Multiple Entries</p>
                    <p className="text-sm text-gray-500">Post as many as you want!</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* WHY "KEEP THE 100" - The Story */}
      <div className="py-20 px-4 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-green-100 text-green-700 border-green-300 mb-4 px-4 py-2">
              <Percent className="w-4 h-4 mr-2" />
              The Movement
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Why "Keep the <span className="text-green-600">100</span>"?
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Other platforms charge you <span className="font-bold text-red-500">2.9% + fees</span> on every card payment. 
              That's YOUR hard-earned money going to someone else. With MyHairChair, when clients pay via 
              <span className="font-bold text-green-600"> CashApp, Zelle, or Cash</span> — you keep <span className="font-bold text-green-600">100% of every dollar</span>.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {whyKeepThe100.map((item, index) => (
              <Card key={index} className="bg-white border-green-200 hover:border-green-400 hover:shadow-lg transition-all">
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <item.icon className="w-10 h-10 text-white" />
                  </div>
                  <Badge className="bg-green-500 text-white border-0 mb-3 text-lg px-4 py-1">{item.highlight}</Badge>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-3xl p-8 sm:p-12 text-center text-white">
            <h3 className="text-2xl sm:text-3xl font-bold mb-4">
              Stop paying fees. Start keeping YOUR money.
            </h3>
            <p className="text-green-100 text-lg mb-6 max-w-2xl mx-auto">
              A stylist charging $50/haircut who does 20 cuts/week loses over <span className="font-bold text-yellow-300">$1,500/year</span> in card fees. 
              That's money that should stay in YOUR pocket.
            </p>
            <div className="flex items-center justify-center gap-4 text-2xl font-bold">
              <span className="text-red-300 line-through">$1,500 in fees</span>
              <ArrowRight className="w-6 h-6" />
              <span className="text-yellow-300">$0 with MyHairChair</span>
            </div>
          </div>
        </div>
      </div>

      {/* Winner Showcase Gallery */}
      <div className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Winners Get the Spotlight</h2>
            <p className="text-gray-600 text-lg">Your work, featured nationwide. Your name, in lights.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {winners.map((winner, index) => (
              <div key={index} className="relative group">
                <div className="rounded-2xl overflow-hidden shadow-xl border-2 border-amber-100 aspect-[3/4]">
                  <img 
                    src={winner.image} 
                    alt={winner.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-6 rounded-b-2xl">
                  <p className="text-white font-bold text-lg">{winner.name}</p>
                  <p className="text-amber-300 text-sm">{winner.location}</p>
                </div>
                {index === 0 && (
                  <div className="absolute top-4 right-4 bg-amber-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                    Featured
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-500">
              Monthly winners are featured on our homepage and all social platforms!
            </p>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="py-20 px-4 bg-amber-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">How to Enter</h2>
            <p className="text-gray-600 text-lg">Three simple steps to compete for cash prizes</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {howItWorks.map((item, index) => (
              <div key={index} className="relative">
                <Card className="bg-white border-gray-200 h-full hover:border-amber-400 hover:shadow-lg transition-all shadow-md">
                  <CardContent className="p-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-500/30">
                      <item.icon className="w-10 h-10 text-white" />
                    </div>
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                      {item.step}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 mt-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </CardContent>
                </Card>
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ChevronRight className="w-8 h-8 text-amber-500" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Instagram CTA - Updated */}
          <div className="mt-12">
            <Card className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 border-0 shadow-2xl overflow-hidden">
              <CardContent className="p-8 sm:p-12">
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="w-24 h-24 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center">
                    <Instagram className="w-14 h-14 text-white" />
                  </div>
                  <div className="text-center md:text-left flex-1">
                    <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">Post to OUR Instagram</h3>
                    <p className="text-white/90 text-lg mb-4">
                      Submit your looks by posting directly to <span className="font-bold">@myhairchairhq</span>
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                      <Badge className="bg-white/20 text-white border-white/30 text-base px-4 py-2">
                        Tag @myhairchairhq
                      </Badge>
                      <Badge className="bg-white/20 text-white border-white/30 text-base px-4 py-2">
                        #KeepThe100LookFest
                      </Badge>
                    </div>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
                    <Repeat className="w-8 h-8 text-white mx-auto mb-2" />
                    <p className="text-white font-bold">Unlimited</p>
                    <p className="text-white/80 text-sm">Entries Allowed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Monthly Prizes */}
      <div className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Monthly Prize Bundle</h2>
            <p className="text-gray-600 text-lg">Win every month from February to September</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {monthlyPrizes.map((prize, index) => (
              <Card 
                key={index} 
                className={`${prize.highlight ? 'bg-gradient-to-br from-amber-400 to-orange-500 border-0 shadow-xl shadow-amber-500/30' : 'bg-white border-gray-200 shadow-lg'}`}
              >
                <CardContent className="p-8 text-center">
                  <prize.icon className={`w-14 h-14 mx-auto mb-4 ${prize.highlight ? 'text-white' : 'text-amber-500'}`} />
                  <p className={`text-xl font-bold ${prize.highlight ? 'text-white' : 'text-gray-900'}`}>{prize.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Badge className="bg-green-100 text-green-700 border-green-300 px-6 py-3 text-base">
              <Zap className="w-5 h-5 mr-2" />
              $100 Cash paid instantly via Zelle or CashApp - You Keep the 100!
            </Badge>
          </div>
        </div>
      </div>

      {/* Grand Finale */}
      <div className="py-20 px-4 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30 mb-4 px-4 py-2">
              <MapPin className="w-4 h-4 mr-2" />
              October 2026 • Houston, TX
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Grand Finale</h2>
            <p className="text-gray-400 text-lg">Emerald Studio Suites • Live Event</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {grandPrizes.map((prize, index) => (
              <Card 
                key={index} 
                className={`bg-gradient-to-br ${prize.color} border-0 shadow-xl ${index === 0 ? 'md:-mt-4 md:scale-105' : ''}`}
              >
                <CardContent className={`p-8 text-center ${prize.textColor}`}>
                  <Trophy className="w-16 h-16 mx-auto mb-4 opacity-80" />
                  <p className="text-2xl font-bold mb-2">{prize.place} Place</p>
                  <p className="text-4xl font-bold mb-2">{prize.cash}</p>
                  <p className="text-lg font-medium opacity-80">+ {prize.access} Platform Access</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Competition Timeline</h2>
            <p className="text-gray-600 text-lg">8 qualifying rounds + Grand Finale</p>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {timeline.map((item, index) => (
              <div 
                key={index}
                className={`px-6 py-4 rounded-xl border-2 ${
                  item.highlight 
                    ? 'bg-gradient-to-br from-amber-500 to-orange-600 border-amber-400 text-white shadow-lg shadow-amber-500/30' 
                    : 'bg-white border-gray-200 text-gray-900 hover:border-amber-400 transition-colors'
                }`}
              >
                <p className="text-lg font-bold">{item.month}</p>
                <p className={`text-sm ${item.highlight ? 'text-white/80' : 'text-gray-500'}`}>{item.status}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Eligibility */}
      <div className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Who Can Enter?</h2>
          </div>

          <div className="grid sm:grid-cols-3 gap-6">
            <Card className="bg-white border-gray-200 hover:border-amber-400 hover:shadow-lg transition-all">
              <CardContent className="p-6 text-center">
                <Scissors className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Barbers</h3>
                <p className="text-gray-600">Show your fade game, beard work, and signature cuts</p>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-200 hover:border-amber-400 hover:shadow-lg transition-all">
              <CardContent className="p-6 text-center">
                <Star className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Stylists</h3>
                <p className="text-gray-600">Braids, color, cuts, styling - all techniques welcome</p>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-200 hover:border-amber-400 hover:shadow-lg transition-all">
              <CardContent className="p-6 text-center">
                <Building2 className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Salon Owners</h3>
                <p className="text-gray-600">Compete yourself or feature your team's best work</p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-500 text-sm">
              * Clients are not eligible to enter. Must be a licensed professional or salon owner.
            </p>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="py-20 px-4 bg-gradient-to-b from-amber-50 to-amber-100">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Ready to <span className="text-amber-500">Keep the 100</span>?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join the movement. Show your work. Win cash. Keep 100% of your fees.
          </p>
          
          {/* Soldier Code Section */}
          {soldierValidation?.valid ? (
            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-2 border-purple-500/30 rounded-2xl p-6 mb-8 max-w-lg mx-auto">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Shield className="w-6 h-6 text-purple-500" />
                <span className="font-bold text-purple-700 text-lg">Soldier Referral Applied!</span>
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              
              {/* Soldier Info Card */}
              <div className="bg-white rounded-xl p-4 mb-4 shadow-sm">
                <p className="text-gray-500 text-sm mb-1">Referred by:</p>
                <p className="text-xl font-bold text-gray-800 mb-2">
                  {soldierValidation.salon_name || "MyHairChair Soldier"}
                </p>
                <div className="flex items-center justify-center gap-2 bg-purple-100 rounded-lg py-2 px-4">
                  <span className="text-purple-600 text-sm">Code:</span>
                  <code className="text-lg font-mono font-bold text-purple-700">{soldierCode}</code>
                </div>
              </div>
              
              {/* Benefits */}
              <div className="flex flex-wrap items-center justify-center gap-3">
                <div className="bg-green-500/20 px-4 py-2 rounded-full flex items-center gap-2">
                  <Gift className="w-4 h-4 text-green-600" />
                  <span className="text-green-700 font-bold">{soldierValidation.trial_days}-Day FREE Trial</span>
                </div>
                <div className="bg-amber-500/20 px-4 py-2 rounded-full flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-amber-600" />
                  <span className="text-amber-700 font-bold">$10 OFF × 3 months</span>
                </div>
              </div>
              
              <p className="text-center text-gray-500 text-xs mt-4">
                This code will be applied automatically at checkout
              </p>
            </div>
          ) : (
            <div className="mb-8">
              {!showCodeInput ? (
                <button
                  onClick={() => setShowCodeInput(true)}
                  className="text-purple-600 hover:text-purple-700 font-medium underline underline-offset-4 flex items-center gap-2 mx-auto"
                >
                  <Gift className="w-4 h-4" />
                  Have a Soldier referral code?
                </button>
              ) : (
                <div className="bg-white border-2 border-purple-200 rounded-2xl p-6 max-w-md mx-auto">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium text-gray-700 flex items-center gap-2">
                      <Shield className="w-4 h-4 text-purple-500" />
                      Enter Soldier Code
                    </span>
                    <button 
                      onClick={() => setShowCodeInput(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="e.g. GEORGE-HQ"
                      value={soldierCode}
                      onChange={(e) => setSoldierCode(e.target.value.toUpperCase())}
                      className="flex-1 text-center font-mono uppercase border-purple-200 focus:border-purple-500"
                      data-testid="soldier-code-input"
                    />
                    <Button
                      onClick={handleApplyCode}
                      disabled={validatingSoldier || !soldierCode.trim()}
                      className="bg-purple-500 hover:bg-purple-600 text-white"
                    >
                      {validatingSoldier ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        "Apply"
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Get 14-day FREE trial + discounted pricing!
                  </p>
                </div>
              )}
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <Button
              size="lg"
              className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-bold text-xl px-10 py-7 shadow-xl shadow-amber-500/30"
              onClick={() => handleEnterContest('stylist')}
              data-testid="lookfest-cta-btn"
            >
              <Trophy className="w-6 h-6 mr-2" />
              Enter Now + {soldierValidation?.valid ? soldierValidation.trial_days : 30} Days Free
            </Button>
          </div>

          <p className="text-gray-500">
            No purchase necessary. {soldierValidation?.valid ? `${soldierValidation.trial_days}-day` : '30-day'} Premium trial included!
          </p>
        </div>
      </div>

      <AppFooter />
    </div>
  );
};

export default LookFestPage;
